import { useCallback, useEffect, useState } from "react";
import { Gift } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { OpsShell } from "@/components/admin/OpsShell";
import { Card, Empty, fmtDate, Loading, Pager, Pill, T } from "@/components/admin/ui";

const STATUSES = ["all", "used", "unused", "revoked"];

const Stat = ({ label, value, testid }) => (
  <div data-testid={testid} className="rounded-xl border border-slate-200 dark:border-slate-800 px-4 py-3">
    <p className={`text-[10px] uppercase tracking-[0.14em] ${T.muted}`}>{label}</p>
    <p className={`text-xl font-semibold ${T.num}`}>{value}</p>
  </div>
);

export default function AdminAppSumo() {
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [d, setD] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: String(page) });
      if (status !== "all") params.set("status", status);
      if (q.trim()) params.set("q", q.trim());
      const { data } = await api.get(`/appsumo/admin/codes?${params}`);
      setD(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [status, q, page]);

  useEffect(() => {
    load();
  }, [load]);

  // near-realtime: redemptions appear within a few seconds without a manual refresh
  useEffect(() => {
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <OpsShell title="AppSumo codes" subtitle="Lifetime deal redemptions, customers and dates">
      {d?.summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <Stat label="Total codes" value={d.summary.total} testid="appsumo-stat-total" />
          <Stat label="Redeemed" value={d.summary.used} testid="appsumo-stat-used" />
          <Stat label="Available" value={d.summary.unused} testid="appsumo-stat-unused" />
          <Stat label="Revoked" value={d.summary.revoked} testid="appsumo-stat-revoked" />
        </div>
      )}
      <Card
        testid="admin-appsumo-panel"
        title="Codes"
        hint={d ? `${d.total} records` : ""}
        actions={
          <div className="flex gap-2">
            <input
              data-testid="admin-appsumo-search"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
              placeholder="Search code or email"
              className={`${T.input} w-56 py-1.5`}
            />
            <select
              data-testid="admin-appsumo-status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
              className={`${T.input} w-36 py-1.5`}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s === "all" ? "All statuses" : s}
                </option>
              ))}
            </select>
          </div>
        }
      >
        {!d ? (
          <Loading label="Loading codes" />
        ) : d.items.length === 0 ? (
          <Empty label="No codes found" hint="Try a different filter" icon={Gift} />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse min-w-[860px]">
                <thead>
                  <tr>
                    <th className={T.th}>Code</th>
                    <th className={T.th}>Status</th>
                    <th className={T.th}>Customer</th>
                    <th className={T.th}>Workspace</th>
                    <th className={T.th}>Redeemed</th>
                  </tr>
                </thead>
                <tbody>
                  {d.items.map((c) => (
                    <tr key={c.code} className={T.row} data-testid={`appsumo-code-row-${c.code}`}>
                      <td className={`${T.td} text-xs ${T.num}`}>{c.code}</td>
                      <td className={T.td}>
                        <Pill value={c.status} />
                      </td>
                      <td className={`${T.td} text-xs ${T.muted}`}>{c.email || "—"}</td>
                      <td className={`${T.td} text-xs ${T.muted}`}>{c.org_name || "—"}</td>
                      <td className={`${T.td} ${T.num} ${T.muted}`}>
                        {c.redeemed_at ? fmtDate(c.redeemed_at) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager page={d.page} pages={d.pages} total={d.total} onPage={setPage} testid="admin-appsumo-pager" />
          </>
        )}
      </Card>
    </OpsShell>
  );
}
